import { createSelector } from "@reduxjs/toolkit";
import { RootState } from "../rootStore";
function getIntervalDate(selectedDateType: string, date = new Date()) {
  let days = 1000;

  if (selectedDateType === "month") {
    days = 31;
  }
  if (selectedDateType === "week") {
    days = 7;
  }

  if (selectedDateType === "year") {
    days = 365;
  }
  const previous = new Date(date.getTime());
  previous.setDate(date.getDate() - days);

  return previous;
}

const sortByDate = (date1:any, date2:any)=>{
  return new Date(date1.createdAt).getTime() - new Date(date2.createdAt).getTime()
}



export const selectCurrentPatient = ({ patient }: RootState) =>
  patient.currentPatient;

export const selectCurrentPatientDailyCheckinData = ({ patient }: RootState) =>
  patient.currentPatientDailyCheckin;

export const selectCurrentPatientDailyCheckinWeekData = ({
  patient,
}: RootState) => patient.currentPatientDailyCheckinWeek;
export const selectCurrentPatientDailyCheckinYearData = ({
  patient,
}: RootState) => patient.currentPatientDailyCheckinYear;

export const selectCurrentClinicPatientsList = ({ patient }: RootState) =>
  patient.clinicPatientsList;

export const selectCurrentClinicPatientsCount = ({ patient }: RootState) =>
  patient.totalClinicPatients;

export const selectClinicHasMoreClinicPatients = ({ patient }: RootState) => {
  return (
    patient.totalClinicPatients >
    patient.offsetClinicPatients + patient.limitClinicPatients
  );
};
export const selectLoading = ({ patient }: RootState) => patient.loading;

export const selectExamList = ({ patient }: RootState) => patient.examList;
export const selectExamDateRange = ({ patient }: RootState) =>
  patient.examDateRange;

export const selectSortConfig = ({ patient }: RootState) => patient.sortConfig;

export const selectSortConfigClinicPatients = ({ patient }: RootState) =>
  patient.sortConfigClinicPatients;

export const selectPatientRegionDetails = ({ patient }: RootState) =>
  patient.patientRegionDetails;

export const selectExamMLResultLeftKnee = ({ patient }: RootState) => {
  return patient.examMLResultLeftKnee;
};

export const selectExamMLResultRightKnee = ({ patient }: RootState) =>
  patient.examMLResultRightKnee;

export const selectExamMLResultDoubleKnee = ({ patient }: RootState) =>
  patient.examMLResultDoubleKnee;

export const selectJointFilterParams = ({ patient }: RootState) =>
  patient.jointFilterParams;

export const selectTypeFilterParams = ({ patient }: RootState) =>
  patient.typeFilterParams;

export const selectHasMoreExams = ({ patient }: RootState) =>
  patient.totalExams > patient.offset + patient.limit;

export const selectPatientSportsUploading = ({ patient }: RootState) =>
  patient.patientSportsUploading;

export const selectAthleteIdInProcess = ({ patient }: RootState) =>
  patient.athleteIdInProcess;

export const selectSportFilterCategories = ({ patient }: RootState) =>
  patient.sportFilterCategories;

export const selectClinicPatientsOffset = ({ patient }: RootState) =>
  patient.offsetClinicPatients;
export const selectClinicPatientsTotal = ({ patient }: RootState) =>
  patient.totalClinicPatients;

export const selectClinicPatientsLimit = ({ patient }: RootState) =>
  patient.limitClinicPatients;

export const selectJointOptions = ({ patient }: RootState) =>
  patient.jointOptions;

export const selectRegionInfo = createSelector(
  selectExamList,
  (exams) => (_id: string) => {
    const filteredExam = exams.find((exam) => {
      return exam.patientRegionId === _id;
    });
    return {
      movement: filteredExam?.movement?.name,
      movementTitle: filteredExam?.movement?.movementTitle,
      side: filteredExam?.joint?.side,
      jointName: filteredExam?.joint?.name,
    };
  }
);
createSelector(selectExamList, (exams) => (_id: string) => {
  const filteredExam = exams.find((exam) => {
    return exam._id === _id;
  });
  return filteredExam;
});
export const selectExamMLResultLeftKneeIds = createSelector(
  selectExamList,
  selectExamDateRange,
  (exams, examDateRange) => {
    const dateToCompare = new Date(getIntervalDate(examDateRange)).getTime();
    const examMLResultLeftKneeIds = exams
      .filter(
        (item) =>
          item?.joint?.side === "left" &&
          new Date(item.createdAt).getTime() >= dateToCompare
      )
      .map((item) => item._id);
    return [...examMLResultLeftKneeIds].sort(sortByDate)
  }
);

export const selectExamMLResultRightKneeIds = createSelector(
  selectExamList,
  selectExamDateRange,
  (exams, examDateRange) => {
    const dateToCompare = new Date(getIntervalDate(examDateRange)).getTime();
    const examMLResultRightKneeIds = exams
      .filter(
        (item) =>
          item?.joint?.side === "right" &&
          new Date(item.createdAt).getTime() >= dateToCompare
      )
      .map((item) => item._id);
    return [...examMLResultRightKneeIds].sort(sortByDate)
  }
);

export const selectExamMLResultLeftKneeLabels = createSelector(
  selectExamList,
  selectExamDateRange,
  (exams, examDateRange) => {
    const dateToCompare = new Date(getIntervalDate(examDateRange)).getTime();
    const examMLResultLeftKnees = exams.filter(
      (item) =>
        item.joint.side === "left" &&
        new Date(item.createdAt).getTime() >= dateToCompare
    );

    return [...examMLResultLeftKnees].sort(sortByDate).map((item) => item?.createdAt ?? "") ?? [];
  }
);

export const selectExamMLResultRightKneeLabels = createSelector(
  selectExamList,
  selectExamDateRange,
  (exams, examDateRange) => {
    const dateToCompare = new Date(getIntervalDate(examDateRange)).getTime();
    const examMLResultRightKnees = exams.filter(
      (item) =>
        item.joint.side === "right" &&
        new Date(item.createdAt).getTime() >= dateToCompare
    );

    return [...examMLResultRightKnees].sort(sortByDate).map((item) => item?.createdAt ?? "") ?? [];
  }
);

export const selectExamMLResultLeftMaxValues = createSelector(
  selectExamList,
  selectExamDateRange,
  (exams, examDateRange) => {
    const dateToCompare = new Date(getIntervalDate(examDateRange)).getTime();
    const examMLResultLeftKnees = exams.filter(
      (item) =>
        item.joint.side === "left" &&
        new Date(item.createdAt).getTime() >= dateToCompare
    );
    return [...examMLResultLeftKnees].sort(sortByDate).map(
      (item) => item?.examMLResult?.leftMaxValue ?? null
    );
  }
);

export const selectExamMLResultRightMaxValues = createSelector(
  selectExamList,
  selectExamDateRange,
  (exams, examDateRange) => {
    const dateToCompare = new Date(getIntervalDate(examDateRange)).getTime();
    const examMLResultRightKnees = exams.filter(
      (item) =>
        item.joint.side === "right" &&
        new Date(item.createdAt).getTime() >= dateToCompare
    );

    return [...examMLResultRightKnees].sort(sortByDate).map(
      (item) => item?.examMLResult?.rightMaxValue ?? null
    );
  }
);

export const selectExamMLResultLeftMinValues = createSelector(
  selectExamList,
  selectExamDateRange,
  (exams, examDateRange) => {
    const dateToCompare = new Date(getIntervalDate(examDateRange)).getTime();
    const examMLResultLeftKnees = exams.filter(
      (item) =>
        item.joint.side === "left" &&
        new Date(item.createdAt).getTime() >= dateToCompare
    );

    return [...examMLResultLeftKnees].sort(sortByDate).map(
      (item) => item?.examMLResult?.leftMinValue ?? null
    );
  }
);

export const selectExamMLResultRightMinValues = createSelector(
  selectExamList,
  selectExamDateRange,
  (exams, examDateRange) => {
    const dateToCompare = new Date(getIntervalDate(examDateRange)).getTime();
    const examMLResultRightKnees = exams.filter(
      (item) =>
        item.joint.side === "right" &&
        new Date(item.createdAt).getTime() >= dateToCompare
    );

    return [...examMLResultRightKnees].sort(sortByDate).map(
      (item) => item?.examMLResult?.rightMinValue ?? null
    );
  }
);
